// ReportManager.jsx - Updated with React Icons
import React, { useState, useEffect } from "react";
import { 
  FiUser, FiMail, FiBriefcase, FiEdit2, FiTrash2, 
  FiRefreshCw, FiSearch, FiX, FiCheck, FiAlertCircle,
  FiPlus, FiHome, FiChevronRight, FiArrowLeft, FiSend
} from "react-icons/fi";
import { 
  RiBuildingLine, RiUserAddLine, RiMailSendLine 
} from "react-icons/ri";
import { 
  HiOutlineOfficeBuilding, HiOutlineUserGroup 
} from "react-icons/hi";
import API_BASE_URL from "../config";
import "./ReportManager.css";

const ReportManager = ({ 
  companyName: propCompanyName,
  onRefreshManagers
}) => {
  const [managersData, setManagersData] = useState([]);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [newManager, setNewManager] = useState({
    manager_id: "", // 🔥 Added manual manager_id field
    name: "",
    email: "",
    department: "",
    company_name: ""
  });
  
  const [editingManagerId, setEditingManagerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("managers");
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const companyName = propCompanyName || sessionStorage.getItem("companyName");

  useEffect(() => {
    if (companyName) {
      fetchManagersData();
      fetchDepartmentsData();
      setNewManager(prev => ({
        ...prev,
        company_name: companyName
      }));
    }
  }, [companyName]);

  const fetchManagersData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      
      const token = sessionStorage.getItem("token");
      
      if (!token) {
        setErrorMessage("Authentication token not found. Please login again.");
        return;
      }

      if (!companyName) {
        setErrorMessage("Company name not found");
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/managers?company_name=${encodeURIComponent(companyName)}`,
        {
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch managers: ${response.status}`);
      }

      const data = await response.json();
      setManagersData(data || []);
      
    } catch (error) {
      console.error("❌ Error fetching managers:", error);
      setErrorMessage(`Failed to load managers: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartmentsData = async () => {
    try {
      const token = sessionStorage.getItem("token");
      
      if (!token || !companyName) return;

      const response = await fetch(
        `${API_BASE_URL}/api/departments?company_name=${encodeURIComponent(companyName)}`,
        {
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDepartmentsData(data || []);
      }
    } catch (error) {
      console.error("❌ Error fetching departments:", error);
    }
  };

  const handleManagerChange = (e) => {
    setNewManager({ 
      ...newManager, 
      [e.target.name]: e.target.value 
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateManagerId = (manager_id) => {
    // Basic validation - can be customized
    return manager_id && manager_id.trim().length >= 3;
  };

  const handleAddManager = async () => {
    // Validate inputs
    if (!newManager.manager_id.trim()) {
      setErrorMessage("Please enter Manager ID");
      return;
    }
    
    if (!validateManagerId(newManager.manager_id)) {
      setErrorMessage("Manager ID must be at least 3 characters");
      return;
    }
    
    if (!newManager.name.trim()) {
      setErrorMessage("Please enter manager name");
      return;
    }
    
    if (!newManager.email.trim()) {
      setErrorMessage("Please enter email address");
      return;
    }
    
    if (!validateEmail(newManager.email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }
    
    if (!newManager.department.trim()) {
      setErrorMessage("Please select department");
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
      
      const token = sessionStorage.getItem("token");
      
      if (!token || !companyName) {
        setErrorMessage("Missing authentication or company information");
        setIsLoading(false);
        return;
      }
      
      console.log("📝 Creating new manager:", newManager);
      
      const response = await fetch(`${API_BASE_URL}/api/managers`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          ...newManager,
          send_email: true
        }),
      });
      
      const result = await response.json();
      console.log("📊 Create manager response:", result);
      
      if (response.ok) {
        // Add new manager to list
        setManagersData(prev => [...prev, result]);
        
        // Reset form
        setNewManager({
          manager_id: "", // Reset manager_id
          name: "",
          email: "",
          department: "",
          company_name: companyName
        });
        
        setSuccessMessage("✅ Manager created successfully! Login credentials have been sent via email.");
        
        // Call refresh callback if provided
        if (onRefreshManagers) {
          onRefreshManagers();
        }
        
        // Switch back to managers list
        setTimeout(() => {
          setActiveTab("managers");
          setSuccessMessage("");
        }, 3000);
        
      } else {
        setErrorMessage(`❌ ${result.message || "Failed to create manager"}`);
      }
      
    } catch (error) {
      console.error("❌ Error creating manager:", error);
      setErrorMessage("❌ Network error creating manager");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditManager = (manager) => {
    setNewManager({
      manager_id: manager.manager_id || "", // Include manager_id
      name: manager.name || "",
      email: manager.email || "",
      department: manager.department || "",
      company_name: manager.company_name || companyName || ""
    });
    setEditingManagerId(manager._id);
    setActiveTab("form");
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleUpdateManager = async () => {
    if (!editingManagerId) {
      setErrorMessage("No manager selected for editing");
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
      
      const token = sessionStorage.getItem("token");
      
      const response = await fetch(`${API_BASE_URL}/api/managers/${editingManagerId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify(newManager),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Update manager in list
        setManagersData(prev => prev.map(mgr => 
          mgr._id === editingManagerId ? result : mgr
        ));
        
        // Reset form
        setNewManager({
          manager_id: "",
          name: "",
          email: "",
          department: "",
          company_name: companyName
        });
        
        setEditingManagerId(null);
        setSuccessMessage("✅ Manager updated successfully!");
        
        // Switch back to managers list
        setTimeout(() => {
          setActiveTab("managers");
          setSuccessMessage("");
        }, 2000);
        
      } else {
        setErrorMessage(`❌ ${result.message || "Failed to update manager"}`);
      }
      
    } catch (error) {
      console.error("❌ Error updating manager:", error);
      setErrorMessage("❌ Error updating manager");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setNewManager({
      manager_id: "",
      name: "",
      email: "",
      department: "",
      company_name: companyName
    });
    setEditingManagerId(null);
    setActiveTab("managers");
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleDeleteManager = async (id) => {
    if (!window.confirm("Are you sure you want to delete this manager? This action cannot be undone.")) {
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
      
      const token = sessionStorage.getItem("token");
      
      const response = await fetch(`${API_BASE_URL}/api/managers/${id}`, {
        method: "DELETE",
        headers: { 
          "Authorization": `Bearer ${token}` 
        }
      });
      
      if (response.ok) {
        // Remove manager from list
        setManagersData(prev => prev.filter(mgr => mgr._id !== id));
        setSuccessMessage("✅ Manager deleted successfully!");
        
        // Call refresh callback if provided
        if (onRefreshManagers) {
          onRefreshManagers();
        }
        
        setTimeout(() => {
          setSuccessMessage("");
        }, 2000);
        
      } else {
        const result = await response.json();
        setErrorMessage(`❌ ${result.message || "Failed to delete manager"}`);
      }
      
    } catch (error) {
      console.error("❌ Error deleting manager:", error);
      setErrorMessage("❌ Error deleting manager");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCredentials = async (managerId) => {
    if (!window.confirm("Send login credentials to this manager?")) {
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
      
      const token = sessionStorage.getItem("token");
      
      const response = await fetch(`${API_BASE_URL}/api/managers/${managerId}/send-credentials`, {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}` 
        }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccessMessage("✅ Login credentials sent successfully!");
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } else {
        setErrorMessage(`❌ ${result.message || "Failed to send credentials"}`);
      }
      
    } catch (error) {
      console.error("❌ Error sending credentials:", error);
      setErrorMessage("❌ Error sending credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchManagersData();
    fetchDepartmentsData();
    setSuccessMessage("🔄 Data refreshed successfully!");
    setTimeout(() => {
      setSuccessMessage("");
    }, 2000);
  };

  // Filter managers based on search term
  const filteredManagers = managersData.filter(manager => {
    if (!manager) return false;
    
    return (
      (manager.manager_id && manager.manager_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (manager.name && manager.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (manager.email && manager.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (manager.department && manager.department.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  if (!companyName) {
    return (
      <div className="rmEnhancedPageWrapper">
        <div className="rmEnhancedErrorState">
          <div className="rmEnhancedErrorIcon">
            <HiOutlineOfficeBuilding size={48} />
          </div>
          <h3>Company Information Missing</h3>
          <p>Unable to load managers. Please check your company settings.</p>
        </div>
      </div>
    );
  }

  if (isLoading && managersData.length === 0) {
    return (
      <div className="rmEnhancedPageWrapper">
        <div className="rmEnhancedLoadingState">
          <div className="rmEnhancedLoadingSpinnerLarge"></div>
          <p>Loading managers data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rmEnhancedPageWrapper">
      {/* Messages Display */}
      {successMessage && (
        <div className="rmEnhancedSuccessMessage">
          <span className="rmEnhancedSuccessIcon">
            <FiCheck />
          </span>
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="rmEnhancedErrorMessage">
          <span className="rmEnhancedErrorIcon">
            <FiAlertCircle />
          </span>
          {errorMessage}
        </div>
      )}

      {/* Header Section */}
      <header className="rmEnhancedHeaderSection">
        <div className="rmEnhancedHeaderContent">
          <h1 className="rmEnhancedMainTitle">
            <span className="rmEnhancedGradientText">Reporting Managers</span> Management
          </h1>
          <p className="rmEnhancedSubtitle">
            Manage your company's reporting structure with ease
            <span className="rmEnhancedCompanyBadge">{companyName}</span>
          </p>
        </div>
        
        <div className="rmEnhancedHeaderActions">
          <button 
            className="rmEnhancedRefreshButton"
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh data"
          >
            <span className="rmEnhancedRefreshIcon">
              <FiRefreshCw />
            </span>
            Refresh
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="rmEnhancedContentWrapper">
        <div className="rmEnhancedMainContainer">
          
          {/* Navigation Tabs */}
          <div className="rmEnhancedTabNavigation">
            <button 
              className={`rmEnhancedTabButton ${activeTab === "managers" ? "rmEnhancedTabActive" : ""}`}
              onClick={() => setActiveTab("managers")}
              disabled={isLoading}
            >
              <span className="rmEnhancedTabIcon">
                <FiUser />
              </span>
              All Managers ({managersData.length})
            </button>
            <button 
              className={`rmEnhancedTabButton ${activeTab === "form" ? "rmEnhancedTabActive" : ""}`}
              onClick={() => setActiveTab("form")}
              disabled={isLoading}
            >
              <span className="rmEnhancedTabIcon">
                <RiUserAddLine />
              </span>
              {editingManagerId ? "Edit Manager" : "Add New Manager"}
            </button>
          </div>

          {/* Managers List Tab */}
          {activeTab === "managers" && (
            <div className="rmEnhancedManagersTab">
              {/* Search Bar */}
              <div className="rmEnhancedSearchContainer">
                <div className="rmEnhancedSearchInputWrapper">
                  {/* <span className="rmEnhancedSearchIcon">
                    <FiSearch />
                  </span> */}
                  <input
                    type="text"
                    placeholder="Search by Manager ID, name, email, or department..."
                    className="rmEnhancedSearchInput"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={isLoading}
                  />
                  {searchTerm && (
                    <button 
                      className="rmEnhancedClearSearch"
                      onClick={() => setSearchTerm("")}
                      disabled={isLoading}
                    >
                      <span className="rmEnhancedClearIcon">
                        <FiX />
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Managers Grid */}
              {isLoading && filteredManagers.length === 0 ? (
                <div className="rmEnhancedLoadingState">
                  <div className="rmEnhancedLoadingSpinnerLarge"></div>
                  <p>Loading managers...</p>
                </div>
              ) : (
                <div className="rmEnhancedManagersGrid">
                  {filteredManagers.map(manager => (
                    <div key={manager._id} className="rmEnhancedManagerCard">
                      <div className="rmEnhancedCardHeader">
                        <div className="rmEnhancedAvatarContainer">
                          <div className="rmEnhancedAvatar">
                            {manager.name ? 
                              manager.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : 
                              'M'
                            }
                          </div>
                          <div className="rmEnhancedStatusIndicator active"></div>
                        </div>
                        <div className="rmEnhancedManagerMainInfo">
                          <h3 className="rmEnhancedManagerName">{manager.name || 'Unnamed Manager'}</h3>
                          <p className="rmEnhancedManagerEmail">{manager.email || 'No email'}</p>
                          <p className="rmEnhancedManagerId">
                            <strong>ID:</strong> {manager.manager_id || 'N/A'}
                          </p>
                        </div>
                        <div className="rmEnhancedCardActions">
                          <button 
                            className="rmEnhancedActionButton rmEnhancedSendButton"
                            onClick={() => handleSendCredentials(manager._id)}
                            disabled={isLoading}
                            title="Resend Credentials"
                          >
                            <RiMailSendLine />
                          </button>
                          <button 
                            className="rmEnhancedActionButton rmEnhancedEditButton"
                            onClick={() => handleEditManager(manager)}
                            disabled={isLoading}
                            title="Edit Manager"
                          >
                            <FiEdit2 />
                          </button>
                          <button 
                            className="rmEnhancedActionButton rmEnhancedDeleteButton"
                            onClick={() => handleDeleteManager(manager._id)}
                            disabled={isLoading}
                            title="Delete Manager"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                      
                      <div className="rmEnhancedCardBody">
                        <div className="rmEnhancedInfoRow">
                          <span className="rmEnhancedInfoLabel">
                            <FiBriefcase />
                            Department:
                          </span>
                          <span className="rmEnhancedInfoValue">{manager.department || 'Not assigned'}</span>
                        </div>
                        <div className="rmEnhancedInfoRow">
                          <span className="rmEnhancedInfoLabel">
                            <RiBuildingLine />
                            Company:
                          </span>
                          <span className="rmEnhancedInfoValue">{manager.company_name || companyName}</span>
                        </div>
                        <div className="rmEnhancedInfoRow">
                          <span className="rmEnhancedInfoLabel">
                            <FiMail />
                            Added:
                          </span>
                          <span className="rmEnhancedInfoValue">
                            {manager.createdAt ? 
                              new Date(manager.createdAt).toLocaleDateString('en-IN') : 
                              'Recently'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {filteredManagers.length === 0 && !isLoading && (
                    <div className="rmEnhancedEmptyState">
                      <div className="rmEnhancedEmptyIllustration">
                        <HiOutlineUserGroup size={64} />
                      </div>
                      <h3 className="rmEnhancedEmptyTitle">No Managers Found</h3>
                      <p className="rmEnhancedEmptyDescription">
                        {searchTerm ? 
                          "No managers match your search criteria." : 
                          "Get started by adding your first reporting manager."
                        }
                      </p>
                      {searchTerm ? (
                        <button 
                          className="rmEnhancedPrimaryButton"
                          onClick={() => setSearchTerm("")}
                        >
                          Clear Search
                        </button>
                      ) : (
                        <button 
                          className="rmEnhancedPrimaryButton"
                          onClick={() => setActiveTab("form")}
                        >
                          Add New Manager
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Form Tab */}
          {activeTab === "form" && (
            <div className="rmEnhancedFormTab">
              <div className="rmEnhancedFormHeader">
                <h2 className="rmEnhancedFormTitle">
                  {editingManagerId ? "Edit Manager Details" : "Create New Manager"}
                </h2>
                <p className="rmEnhancedFormDescription">
                  {editingManagerId 
                    ? "Update the manager information below" 
                    : "Fill in the details to create a new reporting manager. They will receive login credentials via email."}
                </p>
              </div>
              
              <div className="rmEnhancedFormGrid">
                {/* Manager ID Field - ADDED */}
                <div className="rmEnhancedInputGroup">
                  <label className="rmEnhancedInputLabel">
                    <FiUser /> Manager ID *
                  </label>
                  <input 
                    type="text"
                    name="manager_id" 
                    value={newManager.manager_id} 
                    onChange={handleManagerChange}
                    className="rmEnhancedFormInput"
                    disabled={isLoading || editingManagerId}
                    placeholder="Enter unique Manager ID (e.g., MGR001)"
                    title={editingManagerId ? "Manager ID cannot be changed" : ""}
                  />
                  <small className="rmEnhancedInputHint">
                    {editingManagerId 
                      ? "Manager ID cannot be changed" 
                      : "Unique identifier for this manager (min 3 characters)"}
                  </small>
                </div>
                
                <div className="rmEnhancedInputGroup">
                  <label className="rmEnhancedInputLabel">
                    <FiUser /> Manager Name *
                  </label>
                  <input 
                    type="text"
                    name="name" 
                    value={newManager.name} 
                    onChange={handleManagerChange}
                    className="rmEnhancedFormInput"
                    disabled={isLoading}
                    placeholder="Enter manager's full name"
                  />
                </div>
                
                <div className="rmEnhancedInputGroup">
                  <label className="rmEnhancedInputLabel">
                    <FiMail /> Email Address *
                  </label>
                  <input 
                    type="email"
                    name="email" 
                    value={newManager.email} 
                    onChange={handleManagerChange}
                    className="rmEnhancedFormInput"
                    disabled={isLoading}
                    placeholder="manager@company.com"
                  />
                </div>
                
                <div className="rmEnhancedInputGroup">
                  <label className="rmEnhancedInputLabel">
                    <FiBriefcase /> Department *
                  </label>
                  <select 
                    name="department" 
                    value={newManager.department} 
                    onChange={handleManagerChange}
                    className="rmEnhancedFormSelect"
                    disabled={isLoading}
                  >
                    <option value="">Select Department</option>
                    {departmentsData.map(d => (
                      <option key={d._id || d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="rmEnhancedFormActions">
                  {editingManagerId ? (
                    <div className="rmEnhancedEditActions">
                      <button 
                        className="rmEnhancedPrimaryButton rmEnhancedUpdateButton"
                        onClick={handleUpdateManager}
                        disabled={isLoading || !newManager.name || !newManager.email || !newManager.department}
                      >
                        {isLoading ? (
                          <>
                            <div className="rmEnhancedButtonSpinner"></div>
                            Updating...
                          </>
                        ) : (
                          <>
                            <FiCheck /> Update Manager
                          </>
                        )}
                      </button>
                      <button 
                        className="rmEnhancedSecondaryButton"
                        onClick={handleCancelEdit}
                        disabled={isLoading}
                      >
                        <FiX /> Cancel
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="rmEnhancedPrimaryButton rmEnhancedCreateButton"
                      onClick={handleAddManager}
                      disabled={isLoading || !newManager.manager_id || !newManager.name || !newManager.email || !newManager.department}
                    >
                      {isLoading ? (
                        <>
                          <div className="rmEnhancedButtonSpinner"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <FiPlus /> Create Manager
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportManager;